import type { DataGridColumnProps } from '../../DataGrid/DataGrid';
import type { DropdownChangeFn } from '../../Dropdown/Dropdown';
import type { DataGridColumnOptionsProps } from '../DataGridControl';

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

import useDebounceCallback from '../../../hooks/useDebounceCallback';

import Dropdown from '../../Dropdown/Dropdown';

export type DataGridColumnsVisibilityChangeFn = (
  options: DataGridColumnOptionsProps[],
) => void;

export interface ColumnsVisibilityProps {
  /**
   * Grid columns
   */
  columns?: DataGridColumnProps[];
  /**
   * Grid options
   */
  gridOptions?: DataGridColumnOptionsProps[];
  /**
   * Callback to update grid options value on visibility change
   */
  onChange: DataGridColumnsVisibilityChangeFn;
  /**
   * Additional classes
   */
  additionalClasses?: string;
  /**
   * Test id
   */
  testId?: string;
}

const ColumnsVisibility = ({
  columns,
  testId,
  onChange,
  gridOptions,
  additionalClasses = '',
}: ColumnsVisibilityProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<string[]>([]);

  const currentOptions = (columns || [])
    .filter((el) => el.removable)
    .map((el) => ({
      value: el.accessor,
      label: el.label,
    }));

  const handleCurrentValue = useCallback(() => {
    if (!gridOptions) return;

    if (columns?.length) {
      const gridOptionsHide: Record<string, boolean> = {};
      for (const key in gridOptions) {
        gridOptionsHide[gridOptions[key].colId] =
          gridOptions[key].hide || false;
      }

      const res = columns
        .filter((el) => el.removable)
        .map((obj) => obj.accessor)
        .filter((el) => !gridOptionsHide[el]);

      setValue(res);
    }
  }, [columns, gridOptions]);

  useEffect(() => {
    handleCurrentValue();
  }, [handleCurrentValue]);

  const handleChange = useCallback<DropdownChangeFn>(
    (e) => {
      if (!gridOptions) return;

      const selectedColumns = e.target.value;

      setValue(selectedColumns);

      const availableOptions = currentOptions.map((el) => el.value);

      const updateGridOptions = gridOptions?.map((el) => {
        let updateHide = el.hide;
        if (availableOptions.indexOf(el.colId) > -1) {
          updateHide = selectedColumns.indexOf(el.colId) <= -1;
        }
        return {
          ...el,
          hide: updateHide,
        };
      });

      onChange?.(updateGridOptions);
    },
    [onChange, gridOptions, currentOptions],
  );

  const debouncedHandleChange = useDebounceCallback(handleChange, 500);

  return (
    <div
      className={twMerge(
        'w-full lg:w-auto absolute inline-flex items-center z-10 dark:bg-transparent',
        additionalClasses,
      )}
    >
      <label className="mr-2 text-sm md:text-base dark:text-white">
        {t('Global.Columns')}:
      </label>
      <Dropdown
        onChange={debouncedHandleChange}
        options={currentOptions}
        value={value}
        nullable={true}
        multiple={true}
        placeholder={t('Global.SelectColumns')}
        additionalClasses={'w-auto'}
        additionalContainerClasses={
          'h-7 min-h-7 md:h-8 md:min-h-8 w-[250px] py-0.5'
        }
        additionalDropdownClasses={`xl:w-auto xl:min-w-full left-0`}
        filterCallback={async (query, options) => {
          return options.filter((option) =>
            (option.label + ' ' + option.value)
              .toLowerCase()
              .replace(/\s+/g, '')
              .includes(query.toLowerCase().replace(/\s+/g, '')),
          );
        }}
        maxVisibleValues={1}
        maxVisibleValuesText="..."
        {...getTestProps(testId, `columns-remove`, 'testId')}
      />
    </div>
  );
};

export default ColumnsVisibility;
